import { Fragment, useCallback, useEffect, useState } from 'react'
import { I18n } from 'aws-amplify'
import { useDispatch } from 'react-redux'

import { requestPaymentMethods } from '@/actions/operations/paymentMethod'

import {
  getCurrentUser,
  getOperationsPaymentMethods,
  getOperationsPaymentMethodsCount,
  getOperationsLoading
} from '@/reducers/selectors'

import {
  Flex,
  Button,
  Cell,
  Text,
  Column,
  Row,
  Slot,
  Table,
  Loader
} from '@/primitives'

import { Pagination } from '@/elements'

import OrganizationsFilter from '../../Shared/Filters/OrganizationsFilter'
import HeaderV2 from '@/components/Operations/Shared/HeaderV2'

import history from '../../../../history'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_MANAGEMENT
} from '@/Util/PermissionUtils'

function PaymentMethodTable({ state, dispatchState, modulePath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const paymentMethods = getOperationsPaymentMethods()
  const paymentMethodsCount = getOperationsPaymentMethodsCount()
  const loading = getOperationsLoading()

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)
  const [fetchCalled, setFetchCalled] = useState(false)

  const fetchPaymentMethods = useCallback(
    page => {
      const { organizations } = state
      if (organizations.length > 0) {
        dispatch(
          requestPaymentMethods({
            filter: {
              organizationId: organizations
            },
            limit: pageSize,
            offset: page - 1
          })
        )
        setFetchCalled(true)
      }
    },
    [state, pageSize, dispatch]
  )

  const changePage = useCallback(
    newPage => {
      dispatchState({ page: newPage })
      setFetchCalled(false)
    },
    [dispatchState]
  )

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_MANAGEMENT
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.page !== state.page
    ) {
      if (!fetchCalled) {
        fetchPaymentMethods(state.page)
      }
    }
  }, [state, tableState, fetchPaymentMethods, fetchCalled])

  useEffect(() => {
    setTableState({ ...state })
  }, [state])

  const onCreate = () => history.push(`${modulePath}/create`)
  const onEdit = id => history.push(`${modulePath}/update/${id}`)

  return (
    <Fragment>
      <HeaderV2
        title={I18n.get('Manage Payment Methods')}
        buttonText={I18n.get('Add Payment Method')}
        buttonCallback={onCreate}
        buttonIcon={'add'}
        showButton={canEdit}
      >
        <OrganizationsFilter state={state} dispatchState={dispatchState} />
      </HeaderV2>
      <Loader isLoading={loading}>
        {paymentMethodsCount === 0 && (
          <Flex className={'Operations__Table__Empty'}>
            <Text>{I18n.get('No entries')}</Text>
          </Flex>
        )}
        {paymentMethodsCount > 0 && (
          <Table aria-colcount='2' className='Operations__Table'>
            <Slot name='head'>
              <Row>
                <Column>{I18n.get('Name')}</Column>
                <Column></Column>
              </Row>
            </Slot>
            <Slot name='body'>
              {paymentMethods.map(({ id, name }) => (
                <Row key={id}>
                  <Cell columnName={I18n.get('Name')}>{name}</Cell>
                  <Cell className='Operations__Table__Column__Actions'>
                    {canEdit && (
                      <Button
                        variant='info'
                        size='small'
                        iconBefore='edit'
                        onClick={() => onEdit(id)}
                      />
                    )}
                  </Cell>
                </Row>
              ))}
            </Slot>
          </Table>
        )}
        {paymentMethodsCount > pageSize && (
          <Pagination
            type='simple'
            totalItems={paymentMethodsCount}
            totalPages={totalPages}
            setTotalPages={setTotalPages}
            page={tableState.page}
            setPage={changePage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      </Loader>
    </Fragment>
  )
}

export default PaymentMethodTable
